import {
  ACP_REGISTRY_AGENT_PREFIX,
  CORE_ACP_AGENTS,
  canonicalAcpAgentId,
  DEFAULT_ACP_AGENT_IDS,
  localAcpAgentRegistryId,
} from '@crowdex/agent-contracts';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { offeredAcpAgentIds } from '@crowdex/plugin-contracts';
import type { AcpAgentCatalogData } from '@/app/api/acp/agents/route';
import { listApps } from '@/features/apps/apps-client';
import { isLocalMachine } from '@/lib/local-runtime';
import { type NativeAgent, nativeInvoke } from '@/lib/native-desktop';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';

export { DEFAULT_ACP_AGENT_IDS };

const REGISTRY_AGENT_PREFIX = ACP_REGISTRY_AGENT_PREFIX;

export function registryAgentOption(agent: AcpAgentCatalogData): NativeAgent {
  return {
    id: `${REGISTRY_AGENT_PREFIX}${agent.id}`,
    name: agent.name,
    command: agent.id,
    args: [],
    env: {},
  };
}

export function isRegistryAgentOption(agent: Pick<NativeAgent, 'id'>): boolean {
  return agent.id.startsWith(REGISTRY_AGENT_PREFIX);
}

export function registryIdForLocalAgent(agent: Pick<NativeAgent, 'id'>): string | null {
  return localAcpAgentRegistryId(agent.id);
}

export function acpAgentPreferenceId(agent: Pick<NativeAgent, 'id'>): string {
  if (isRegistryAgentOption(agent)) return agent.id.slice(REGISTRY_AGENT_PREFIX.length);
  return registryIdForLocalAgent(agent) ?? agent.id;
}

export function selectedAcpAgentIds(agentIds: readonly string[] | null): ReadonlySet<string> {
  return new Set((agentIds ?? DEFAULT_ACP_AGENT_IDS).map(canonicalAcpAgentId));
}

export function filterSelectedAcpAgents(
  agents: readonly NativeAgent[],
  agentIds: readonly string[] | null,
): NativeAgent[] {
  const selected = selectedAcpAgentIds(agentIds);
  return agents.filter((agent) => selected.has(acpAgentPreferenceId(agent)));
}

/**
 * Combines discovered and on-demand agents into the list shown by the runtime.
 * Local and registry variants are both retained: runtime-specific selectors
 * project the appropriate one, so an installed local binary never hides its
 * on-demand cloud counterpart.
 */
export function mergeSelectedAcpAgents(
  discoveredAgents: readonly NativeAgent[],
  registryAgents: readonly NativeAgent[],
  agentIds: readonly string[] | null,
): NativeAgent[] {
  const discovered = filterSelectedAcpAgents(discoveredAgents, agentIds);
  return [...discovered, ...filterSelectedAcpAgents(registryAgents, agentIds)];
}

function fetchAcpRegistryAgents(): Promise<AcpAgentCatalogData[]> {
  if (isLocalMachine()) return nativeInvoke<AcpAgentCatalogData[]>('acp_agent_catalog');
  return apiFetch<AcpAgentCatalogData[]>('/api/acp/agents');
}

/**
 * The registry agents offered: the core's own (Claude Code, Codex, OpenCode), and those the
 * user's installed plugins bring (an `acp.<id>` plugin from the catalog, a plugin contributing
 * the agent, or an enabled app declaring it). An unavailable inventory offers the whole registry.
 */
export async function fetchAcpAgentCatalog(): Promise<AcpAgentCatalogData[]> {
  const [agents, offered, apps] = await Promise.all([
    fetchAcpRegistryAgents(),
    nativePluginClient.list().then(offeredAcpAgentIds, () => null),
    listApps().catch(() => null),
  ]);
  if (offered === null) return agents;
  // The core's own agents are always offered; the others come with a plugin.
  for (const agent of CORE_ACP_AGENTS) offered.add(agent.id);
  for (const app of apps ?? []) if (app.enabled) for (const id of app.agents) offered.add(id);
  return agents.filter((agent) => offered.has(agent.id));
}

export async function fetchAcpAgentOptions(): Promise<NativeAgent[]> {
  return (await fetchAcpAgentCatalog()).map(registryAgentOption);
}
